import type { RangeResult } from '../../lib/api/hooks.js';
import { SectionHead } from '../../components/ui/Primitives.js';

/**
 * The credentials for a whole block of new seats.
 *
 * This screen exists because of one constraint: a SIP password is shown exactly
 * once. That is workable for a single extension -- copy it into the handset and
 * move on -- and completely unworkable for fifty, which is why creating a range
 * without somewhere to capture the output would be a feature that loses data
 * every time it is used.
 *
 * So the download is not a convenience here, it is the point of the screen. The
 * table is for checking; the CSV is for keeping.
 */

/**
 * RFC 4180 quoting.
 *
 * The generated passwords avoid quotes and commas, so this is not strictly
 * needed for them today -- but a display name is free text a person typed, and
 * "Reception, front desk" would silently shift every column after it in a file
 * somebody is going to trust with credentials.
 */
function csvCell(v: string): string {
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function toCsv(result: RangeResult, server: string): string {
  // sip_server, typed as the server and the domain both (6 October 2026).
  const head = ['extension', 'display_name', 'sip_username', 'sip_password', 'sip_server'];
  const lines = result.data.map((e) => [
    e.number,
    e.display_name,
    e.sip_credential?.username ?? '',
    e.sip_credential?.password ?? '',
    server,
  ].map(csvCell).join(','));
  /**
   * CRLF and a BOM, because this file is opened in Excel far more often than in
   * anything else. Without the BOM Excel reads it as the local codepage and
   * mangles any non-ASCII name; without CRLF older versions run the rows
   * together.
   */
  return '﻿' + [head.join(','), ...lines].join('\r\n') + '\r\n';
}

export function RangeCredentials({ result, server, onDone }: {
  result: RangeResult;
  /** The one SIP name every phone is given (ARC_SIP_REGISTRAR_HOST), once the account has loaded. */
  server: string | null;
  onDone: () => void;
}) {
  const save = (): void => {
    const blob = new Blob([toCsv(result, server ?? '')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const first = result.data[0]?.number ?? 'extensions';
    const last = result.data[result.data.length - 1]?.number ?? '';
    a.download = `extensions-${first}${last && last !== first ? `-${last}` : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
      <SectionHead level="section" title={<>{result.count} extension{result.count === 1 ? '' : 's'} created</>} />

      <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
        <span>
          These passwords are shown <strong>once</strong> and cannot be read back.
          Download the file before leaving this page — there is no way to
          produce it again, and each seat would have to be reset individually.
        </span>
      </div>

      <div className="arc-row" style={{ gap: 8, marginBottom: 14 }}>
        <button type="button" className="arc-btn arc-btn-primary" onClick={save}>
          Download credentials (CSV)
        </button>
        <button type="button" className="arc-btn arc-btn-ghost" onClick={onDone}>
          Done
        </button>
      </div>

      <div className="arc-table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
        <table className="arc-table">
          <thead>
            <tr>
              <th>Extension</th><th>Name</th><th>SIP username</th><th>Password</th>
            </tr>
          </thead>
          <tbody>
            {result.data.map((e) => (
              <tr key={e.id}>
                <td className="arc-mono">{e.number}</td>
                <td>{e.display_name}</td>
                <td className="arc-mono">{e.sip_credential?.username ?? '—'}</td>
                <td className="arc-mono" style={{ userSelect: 'all', fontWeight: 600 }}>
                  {e.sip_credential?.password ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="arc-hint" style={{ marginTop: 10 }}>
        SIP server: <span className="arc-mono">{server ?? '—'}</span>, as the server and the domain on every handset.
      </p>
    </div>
  );
}
